// Product interfaces — keep these as they define the shape used across the app

export interface ProductVariant {
  label: string;
  price: number;
}

export interface KeyFeature {
  text: string;
  visible: boolean;
}

export interface Product {
  id: string;
  title: string;
  price: number;
  compareAtPrice?: number;
  image: string;
  images?: string[];
  category: string;
  tag: string;
  rating: number;
  reviews: number;
  badge?: string;
  tags?: string[];
  featured?: boolean;
  description?: string;
  shortDescription?: string;
  slug?: string;
  variants?: ProductVariant[];
  packOptions?: string[];
  inventoryCount?: number;
  subOptions?: ProductVariant[];
  defaultVariant?: string;
  promoLabel?: string;
  promoExpires?: string;
  keyFeatures?: KeyFeature[];
  // Egyptian-Arabic SEO content — auto-generated in the background.
  // Always fall back to the English title/description if these are
  // empty (e.g. a brand-new product whose generation hasn't run yet).
  titleAr?: string;
  descriptionAr?: string;
  seoKeywordsAr?: string;
}

// No demo products — all products are managed via the Supabase admin panel
export const allProducts: Product[] = [];

export const allTags: string[] = [];
export const allCategories: string[] = [];

// Picks the right-language title, falling back to English if the Arabic
// content hasn't been generated yet (e.g. a product added moments ago).
// Takes `lang` explicitly rather than calling useTranslation() itself,
// since this is a plain data helper, not a component/hook.
export const getLocalizedTitle = (product: Pick<Product, 'title' | 'titleAr'>, lang: string): string =>
  (lang === 'ar' && product.titleAr) ? product.titleAr : product.title;

export const getLocalizedDescription = (product: Pick<Product, 'description' | 'descriptionAr'>, lang: string): string | undefined =>
  (lang === 'ar' && product.descriptionAr) ? product.descriptionAr : product.description;

// Canonical storefront path for a product. Prefers the admin-set SEO slug
// and falls back to the database id when no slug is set — so existing
// /product/<id> links never break, while new/updated products with a slug
// get the nicer URL everywhere a product link is built.
export const getProductPath = (product: Pick<Product, 'id' | 'slug'>): string =>
  `/product/${product.slug || product.id}`;

// Image transformation temporarily disabled — was routing images through
// Supabase's /render/image/ endpoint which counts toward Storage Egress,
// pushing the free tier over its 5 GB/month limit. Returning the original
// URL directly until images are migrated to Cloudflare R2 (zero egress).
export const getOptimizedImageUrl = (url: string | undefined, _width: number): string => {
  if (!url) return '';
  return url;
};
